import React from 'react';

/* ============================================================================
   FlexiOss Editorial Layout Systems
   Premium, bullet-free presentation primitives. No <ul>/<li>, no discs,
   no checkmark icons — scannability is driven purely by typography, spatial
   rhythm, hairline rules and a single brand accent.
   Theme-aware: relies on `border-white/10`, `bg-slate-900`, `text-*` which
   the light-theme override layer remaps automatically.
   ========================================================================== */

const normalize = (item) =>
  typeof item === 'string' ? { desc: item } : item;

/* ---- System A: Horizontal Border Stack --------------------------------- */
/* Numbered (optional) hairline-separated rows. Use for sequential lists such
   as clinical applications, set contents and guidance points. */
export const HorizontalBorderStack = ({
  items = [],
  accent = '#3b82f6',
  numbered = true,
  dense = false,
  testid = 'border-stack',
}) => (
  <div data-testid={testid}>
    {items.map((raw, i) => {
      const it = normalize(raw);
      return (
        <div
          key={i}
          data-testid={`${testid}-item-${i}`}
          className={`group relative grid grid-cols-[auto_1fr] items-start gap-x-5 ${
            dense ? 'py-3' : 'py-4'
          } ${i > 0 ? 'border-t border-white/10' : ''} transition-colors duration-200`}
        >
          <span
            aria-hidden="true"
            className="absolute left-0 top-0 h-full w-px origin-top scale-y-0 transition-transform duration-300 group-hover:scale-y-100"
            style={{ backgroundColor: accent }}
          />
          {numbered ? (
            <span
              className="pt-0.5 font-mono text-xs font-bold tabular-nums tracking-tight transition-transform duration-300 group-hover:translate-x-1.5"
              style={{ color: accent }}
            >
              {String(i + 1).padStart(2, '0')}
            </span>
          ) : (
            <span
              className="mt-2 h-3 w-px transition-transform duration-300 group-hover:translate-x-1.5"
              style={{ backgroundColor: accent }}
            />
          )}
          <div className="transition-transform duration-300 group-hover:translate-x-1">
            {it.title && (
              <h4 className="text-base font-semibold leading-snug text-white">{it.title}</h4>
            )}
            {it.desc && (
              <p
                className={`text-sm leading-relaxed text-slate-400 ${
                  it.title ? 'mt-1' : ''
                }`}
              >
                {it.desc}
              </p>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

/* ---- System B: Editorial Column Split ---------------------------------- */
/* Asymmetric term / definition pairs. `def` may be a string or a node
   (e.g. a nested HorizontalBorderStack). Use for composition breakdowns. */
export const EditorialColumnSplit = ({
  items = [],
  accent = '#3b82f6',
  testid = 'column-split',
}) => (
  <div data-testid={testid}>
    {items.map((it, i) => (
      <div
        key={i}
        data-testid={`${testid}-item-${i}`}
        className={`grid gap-y-3 py-7 md:grid-cols-[minmax(0,0.85fr)_minmax(0,1.6fr)] md:gap-x-10 ${
          i > 0 ? 'border-t border-white/10' : ''
        }`}
      >
        <div className="border-l-2 pl-4" style={{ borderColor: accent }}>
          <h4 className="text-lg font-bold leading-tight tracking-tight text-white">
            {it.term}
          </h4>
          {it.role && (
            <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              {it.role}
            </p>
          )}
        </div>
        <div className="text-sm leading-relaxed text-slate-400">
          {typeof it.def === 'string' ? <p>{it.def}</p> : it.def}
        </div>
      </div>
    ))}
  </div>
);

/* ---- System C: Grid-Gap Definition Matrix ------------------------------ */
/* Hairline-gap matrix of term + definition cells. The separation is created
   purely by the 1px grid gap. Use for advantage / feature matrices. */
export const GridGapDefinitionMatrix = ({
  items = [],
  accent = '#3b82f6',
  columns = 3,
  testid = 'grid-matrix',
}) => {
  const colClass =
    columns === 2
      ? 'sm:grid-cols-2'
      : columns === 4
      ? 'sm:grid-cols-2 lg:grid-cols-4'
      : 'sm:grid-cols-2 lg:grid-cols-3';
  return (
    <div
      data-testid={testid}
      className={`grid grid-cols-1 ${colClass} gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10`}
    >
      {items.map((it, i) => (
        <div
          key={i}
          data-testid={`${testid}-item-${i}`}
          className="group relative bg-slate-900 p-7 transition-colors duration-200 hover:bg-slate-800/60"
        >
          <div className="mb-4 flex items-center gap-3">
            <span
              className="font-mono text-xs font-bold tabular-nums"
              style={{ color: accent }}
            >
              {String(i + 1).padStart(2, '0')}
            </span>
            <span
              className="h-px flex-1 origin-left transition-transform duration-300 group-hover:scale-x-110"
              style={{ backgroundColor: `${accent}40` }}
            />
          </div>
          <h4 className="mb-2 text-base font-semibold leading-snug text-white">
            {it.term}
          </h4>
          <p className="text-sm leading-relaxed text-slate-400">{it.def}</p>
        </div>
      ))}
    </div>
  );
};
